import { SignUpConfirmationLayout } from '../SignUpConfirmationLayout/SignUpConfirmationLayout.component';
import { SignUpForm } from '../SignUpForm/SignUpForm.component';
import { InscriptionProPageProps } from 'pages/inscription-pro';
import { RegistrationProcess, Resend } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { SignUpProfile } from '@auth/business/auth.utils';
import { useAuthModal } from '@auth/business/AuthModal.hook';
import { UnderlinedButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageHeader } from '@layout/components/PlcPageHeader/PlcPageHeader.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { Constants } from '@utils/Constants';
import { handleScrollTop } from '@utils/navigation';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

type Props = ComponentPropsWithRef<'main'> & InscriptionProPageProps;

const SignUpProLayout: FC<Props> = ({ teamInviteToken, inviteEmail, className, ...rest }) => {
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const [confirmationMode, setConfirmationMode] = useState<Resend | undefined>();
  const { showSignInModal } = useAuthModal();

  function handleDisplayConfirmation(resend: Resend) {
    // In case of an invitation the user is directly redirected to welcome page
    if (resend.registrationProcess !== RegistrationProcess.Invitation) {
      setConfirmationMode(resend);
      handleScrollTop();
    }
  }

  const rootClass = classnames('sign-up-layout', 'plc-page', className);
  const sectionClassName = classnames('wrapper-s', 'main-section');
  const titleClass = useClassNames({ all: 'plc-mb-basis', m: 'plc-mb-l' });

  const isAffiliate = !!teamInviteToken;

  const tagProps: PlcTagProps = useMemo(
    () => ({
      level2: ATLevel2.SignUp,
      tagPageNameI18nArray: ['auth.pro.tag.title', 'auth.sign-up.pro.tag.title'],
    }),
    []
  );

  if (confirmationMode) {
    return <SignUpConfirmationLayout isMobile={isMobile} resend={confirmationMode} />;
  }

  return (
    <PlcPageLayout className={rootClass} metaTitleI18n="auth.sign-up.pro.tag.title" tagProps={tagProps} {...rest}>
      <PlcPageHeader className="plc-mb-s" heroImgSrc="inscription">
        <Text className={titleClass} tag="h1" variant={isMobile ? 'h3' : 'h2'}>
          <Trans
            i18nKey={isAffiliate ? 'auth.sign-up-pro.affiliate.page-title' : 'auth.sign-up-pro.page-title'}
            ns="auth"
          >
            Account creation
            <Text flavour="yellow" tag="span" variant={isMobile ? 'h3' : 'h2'}>
              Pro
            </Text>
          </Trans>
        </Text>

        <Text
          i18nKey={isAffiliate ? 'auth.sign-up-pro.affiliate.page-subtitle' : 'auth.sign-up-pro.page-subtitle'}
          i18nOptions={isAffiliate ? undefined : { price: Constants.PRICE }}
          tag="h2"
          variant={isMobile ? 'h5' : 'h4'}
        />
      </PlcPageHeader>
      <section className="wrapper-s plc-mb-l">
        <div className="plc-container-not-full-width">
          <Text className="plc-mb-m" variant="p">
            <Trans
              i18nKey={isAffiliate ? 'auth.sign-up-pro.affiliate.body-top' : 'auth.sign-up-pro.body-top'}
              ns="auth"
            />
          </Text>
          {!isAffiliate && (
            <Text className="plc-mb-l" variant="p">
              <Text i18nKey="auth.is-signed-up" tag="span" variant="small" />
              <UnderlinedButton
                onClick={() => showSignInModal()}
                textProps={{ flavour: 'red', i18nKey: 'auth.sign-in.your-account', variant: 'small' }}
              />
            </Text>
          )}
        </div>
      </section>

      <section className={sectionClassName}>
        <SignUpForm
          active={true}
          className="plc-container-not-full-width"
          defaultEmail={inviteEmail}
          onSignUpComplete={handleDisplayConfirmation}
          profile={SignUpProfile.pro}
          registrationProcess={isAffiliate ? RegistrationProcess.Invitation : RegistrationProcess.Sec}
          teamInviteToken={teamInviteToken}
          withWrapper={false}
        />
      </section>
    </PlcPageLayout>
  );
};

export { SignUpProLayout };
